import React, { useCallback, useState } from "react";
import { find } from "lodash";
import { Note } from "@/features/workspace/graphql/generated";
import {
  WisgptContextTokenQueryVariables,
  useWisgptContextTokenQuery,
} from "@/shared/services/api/graphql/generated";
import { SummaryToolSection } from "@/shared/widgets/SummaryToolSection";

import { IconButton } from "@/shared/ui/IconButton";
import { Title } from "@/shared/ui/Title";
import { Row, Column } from "@/shared/ui/Blocks";
import { useGpt } from "@/shared/contexts/gptgateway.context";

import {
  FlowEvent,
  ChatEvent,
  LLMEvent,
  Message,
} from "@/shared/hooks/gpt/useGptFlow";
import { EVENT_NAMES, trackAppEvent } from "@/shared/utils/events/trackEvent";
import { useTranslation } from "react-i18next";
import { CustomTagsData } from "../MarkdownText/_components/MarkdownComponents";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

// Type guard
const isMessage = (item: any): item is Message => item?.type === "message";

export interface SummarySection {
  title: string;
  type: string;
  gpt_flow_id: string;
  value?: string;
  title_icon?: string;
}

interface SummarySectionsGroupProps {
  sections: SummarySection[];
  notes: Note[];
  onDeleteSummary: (type: string, id: number) => (content: string) => void;
  onSaveSummary: (type: string, id: number) => (content: string) => void;
  scope: WisgptContextTokenQueryVariables & {
    survey_config: string[];
  };
  loading: boolean;
  useSingleGenerator?: boolean;
  tagsData?: CustomTagsData;
}

function SummarySectionsGroup({
  sections,
  notes,
  onDeleteSummary,
  onSaveSummary,
  scope,
  loading,
  useSingleGenerator = false,
  tagsData,
}: SummarySectionsGroupProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const { initializeFlow, runFlow, socket } = useGpt();

  const { data } = useWisgptContextTokenQuery({
    variables: scope,
  });
  const signedScope = data?.wisgptContextToken || "";

  const handleGenerateAllSummaries = useCallback(async () => {
    if (useSingleGenerator && sections.length > 1) {
      setIsGenerating(true);
      let completedCount = 0;

      // Generate summaries for all sections sequentially
      for (const section of sections) {
        const summary = find(notes, { type: section.type });
        const response = await initializeFlow(section.gpt_flow_id, signedScope);

        if (response) {
          let sectionContent = "";

          // Create a unique handler for this section
          const eventHandler = (evt: FlowEvent) => {
            if (evt.type === "chatEvent") {
              const llmEvent: LLMEvent = (evt as ChatEvent).chatEvent;
              if (llmEvent.event === "full_output_block") {
                // Store the complete content when we receive the full block
                if (llmEvent.item && isMessage(llmEvent.item)) {
                  sectionContent = llmEvent.item.content;
                }
              } else if (llmEvent.event === "on_llm_end") {
                // Save the accumulated content
                onSaveSummary(
                  section.type,
                  summary?.id as number
                )(sectionContent);
                // Remove the event listener after completion
                socket?.off(response.conversationId);

                completedCount++;
                // Only set isGenerating to false when all summaries are complete
                if (completedCount === sections.length) {
                  setIsGenerating(false);
                }
              }
            }
          };

          socket?.on(response.conversationId, eventHandler);
          runFlow({
            runNodeId: response.runnableNodes[0],
            conversationId: response.conversationId,
          });

          trackAppEvent(EVENT_NAMES.GENERATE_GPT_SUMMARY, {
            flowId: section.gpt_flow_id,
          });
        }
      }
    }
  }, [
    useSingleGenerator,
    sections,
    notes,
    onSaveSummary,
    scope,
    initializeFlow,
    signedScope,
    socket,
    runFlow,
  ]);

  const { t } = useTranslation();

  // If there are multiple sections with single generator enabled
  if (useSingleGenerator) {
    return (
      <Column className="gap-8">
        <section className="flex flex-col">
          <Row className="items-center justify-between border-b border-b-solid border-grey-300 pb-4 mb-4">
            <Title as="h3" color="blue" icon="Clipboard">
              {t("profile.executive_summary")}
            </Title>
            <Row className="gap-4">
              <IconButton
                variant="outline"
                color="purple"
                size="md"
                icon="Magicwand"
                label="Generate summary"
                className="bg-purple-100 text-purple-500 hover:bg-purple-100 hover:border-purple-500"
                onClick={handleGenerateAllSummaries}
                disabled={isGenerating}
                loading={isGenerating}
              />
            </Row>
          </Row>
          <Column className="pt-6 gap-12">
            {sections.map((section) => {
              const summary = find(notes, { type: section.type });
              return (
                <SummaryToolSection
                  key={section.title}
                  onDeleteSummary={() =>
                    onDeleteSummary(section.type, summary?.id as number)("")
                  }
                  onSaveSummary={onSaveSummary(
                    section.type,
                    summary?.id as number
                  )}
                  titleAs="h5"
                  summaryContent={summary?.text || ""}
                  loading={loading}
                  deleting={false}
                  scope={scope}
                  flowId={section.gpt_flow_id}
                  title={section.title}
                  titleIcon={section.title_icon as any}
                  hideGenerateButton
                  tagsData={tagsData}
                />
              );
            })}
          </Column>
        </section>
      </Column>
    );
  }

  return (
    <Column className="gap-8">
      <section className="flex flex-col">
        <Column className="pt-6">
          {sections.map((section) => {
            const summary = find(notes, { type: section.type });
            return (
              <SummaryToolSection
                key={section.title}
                onDeleteSummary={() =>
                  onDeleteSummary(section.type, summary?.id as number)("")
                }
                onSaveSummary={onSaveSummary(
                  section.type,
                  summary?.id as number
                )}
                summaryContent={summary?.text || ""}
                loading={loading}
                deleting={false}
                scope={scope}
                flowId={section.gpt_flow_id}
                title={section.title}
                titleIcon={section.title_icon as any}
                tagsData={tagsData}
              />
            );
          })}
        </Column>
      </section>
    </Column>
  );
}

export { SummarySectionsGroup };
